/**
 * WebSocket Command Service — Browser fallback for MQTT
 *
 * When running in a browser (not native Android), MQTT may not be available.
 * This service connects via socket.io to the API's /resident namespace
 * and listens for device.command events, feeding them into the same
 * command pipeline as MQTT.
 */
import { io, Socket } from 'socket.io-client';
import { dispatchCommand } from './MqttTelemetry';
import type { MqttCommand } from './MqttTelemetry';

const API_URL = (import.meta as any).env?.VITE_API_URL || '';

let socket: Socket | null = null;
let currentUserId: number | null = null;

export interface WsCommandConfig {
  userId: number;
  getToken: () => string | null;
}

export function connectWebSocketCommands(config: WsCommandConfig) {
  if (socket) return; // Already connected
  if (!API_URL) {
    console.warn('[WsCommands] No API URL configured, skipping WebSocket connection');
    return;
  }

  currentUserId = config.userId;
  console.log('[WsCommands] Connecting to', API_URL, '/resident as user', config.userId);

  socket = io(`${API_URL}/resident`, {
    transports: ['websocket', 'polling'],
    auth: {
      token: config.getToken(),
    },
    reconnection: true,
    reconnectionDelay: 3000,
    reconnectionDelayMax: 15000,
  });

  socket.on('connect', () => {
    console.log('[WsCommands] Connected, subscribing to user room');
    socket!.emit('subscribe', { personId: config.userId });
  });

  socket.on('device.command', (data: any) => {
    try {
      console.log('[WsCommands] Command received:', data);
      const cmd: MqttCommand = {
        id: data.id || `ws-${Date.now()}`,
        type: data.type,
        payload: data.payload || {},
        timestamp: data.timestamp || new Date().toISOString(),
        source: data.source || 'websocket',
      };

      if (!cmd.type) {
        console.warn('[WsCommands] Invalid command (missing type):', data);
        return;
      }

      dispatchCommand(cmd);
    } catch (e) {
      console.error('[WsCommands] Error processing command:', e);
    }
  });

  socket.on('disconnect', (reason) => {
    console.log('[WsCommands] Disconnected:', reason);
  });

  socket.on('connect_error', (err) => {
    console.warn('[WsCommands] Connection error:', err.message);
  });
}

export function disconnectWebSocketCommands() {
  if (socket) {
    if (currentUserId) {
      socket.emit('unsubscribe', { personId: currentUserId });
    }
    socket.disconnect();
    socket = null;
    currentUserId = null;
    console.log('[WsCommands] Disconnected');
  }
}
